import XLSX from 'xlsx-js-style';

function safe_csv_filename(name) {
  return (name || 'personal_profile_forms')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase() || 'personal_profile_forms';
}

function normalize_sheet_value(value) {
  return `${value ?? ''}`;
}

function get_column_letter(column_index) {
  let column = '';
  let index = column_index + 1;

  while (index > 0) {
    const remainder = (index - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    index = Math.floor((index - 1) / 26);
  }

  return column;
}

function get_default_user_name(user) {
  return user?.email || 'Unknown user';
}

function get_default_location(row) {
  return row?.location || '-';
}

function get_default_date_of_birth(row) {
  return row?.date_of_birth || '-';
}

function get_default_race(row) {
  return row?.race || '-';
}

function get_default_gender(row) {
  return row?.gender || '-';
}

function get_default_answer_cell(row, question_id) {
  return Reflect.get(row?.answers || {}, String(question_id)) || ({ text: '-' });
}

function build_sheet_rows(form_table, options = {}) {
  const questions = form_table.questions || [];
  const {
    getUserName: get_user_name = get_default_user_name,
    getLocation: get_location = get_default_location,
    getDateOfBirth: get_date_of_birth = get_default_date_of_birth,
    getRace: get_race = get_default_race,
    getGender: get_gender = get_default_gender,
    getAnswerCell: get_answer_cell = get_default_answer_cell
  } = options;

  const headers = [
    'User',
    'Email',
    'Location',
    'Date of Birth',
    'Race',
    'Gender',
    ...questions.map((question) => question.question)
  ];

  const rows = form_table.rows.map((row) => {
    const answer_values = questions.map(
      (question) => get_answer_cell(row, question.id).text
    );

    return [
      get_user_name(row.user),
      row.user?.email || '',
      get_location(row),
      get_date_of_birth(row),
      get_race(row),
      get_gender(row),
      ...answer_values
    ].map(normalize_sheet_value);
  });

  return [headers, ...rows];
}
function get_border(color) {
  const side_style = { style: 'thin', color: { rgb: color } };

  return Object.fromEntries(
    ['top', 'right', 'bottom', 'left'].map((side) => [side, side_style])
  );
}

function get_header_style() {
  const font = { bold: true, color: { rgb: 'FFFFFF' } };
  const fill = { fgColor: { rgb: '0F766E' } };
  const alignment = { horizontal: 'center', vertical: 'center', wrapText: true };

  return ({ font, fill, alignment, border: get_border('B7C6CC') });
}

function get_body_style() {
  const alignment = Object.fromEntries([
    ['vertical', 'top'],
    ['wrapText', true]
  ]);

  return Object.fromEntries([
    ['alignment', alignment],
    ['border', get_border('D9E2E7')]
  ]);
}

function get_alternate_row_style(body_style) {
  const fill = Object.fromEntries([
    ['fgColor', { rgb: 'F8FAFC' }]
  ]);

  return Object.fromEntries([
    ...Object.entries(body_style),
    ['fill', fill]
  ]);
}

function get_sheet_styles() {
  const body_style = get_body_style();

  return ({
    header_style: get_header_style(),
    body_style,
    alternate_row_style: get_alternate_row_style(body_style)
  });
}

function get_cell_style(row_index, styles) {
  if (row_index === 0) {
    return styles.header_style;
  }

  return row_index % 2 === 0 ? styles.alternate_row_style : styles.body_style;
}

function apply_worksheet_styles(worksheet) {
  const range = XLSX.utils.decode_range(Reflect.get(worksheet, '!ref'));
  const styles = get_sheet_styles();

  for (let row_index = range.s.r; row_index <= range.e.r; row_index += 1) {
    for (let column_index = range.s.c; column_index <= range.e.c; column_index += 1) {
      const cell_address = `${get_column_letter(column_index)}${row_index + 1}`;
      const cell = Reflect.get(worksheet, cell_address);

      if (cell) {
        Reflect.set(cell, 's', get_cell_style(row_index, styles));
      }
    }
  }
}

function get_row_value(row, index) {
  return normalize_sheet_value(Reflect.get(row, index));
}

function get_worksheet_columns(sheet_rows) {
  const headers = sheet_rows.at(0) || [];

  return headers.map((header, index) => {
    const max_length = sheet_rows.reduce((length, row) => {
      return Math.max(length, get_row_value(row, index).length);
    }, normalize_sheet_value(header).length);

    return { wch: Math.min(Math.max(max_length + 2, index < 2 ? 18 : 24), 48) };
  });
}

function configure_worksheet(worksheet, sheet_rows) {
  apply_worksheet_styles(worksheet);
  Reflect.set(worksheet, '!cols', get_worksheet_columns(sheet_rows));
  Reflect.set(worksheet, '!rows', [{ hpt: 28 }]);
  Reflect.set(worksheet, '!autofilter', { ref: Reflect.get(worksheet, '!ref') });
}

export function export_personal_profile_form_table_csv(form_table, options = {}) {
  if (!form_table?.rows?.length) {
    return;
  }

  const sheet_rows = build_sheet_rows(form_table, options);
  const worksheet = XLSX.utils.aoa_to_sheet(sheet_rows);
  const workbook = XLSX.utils.book_new();

  configure_worksheet(worksheet, sheet_rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Personal Forms');
  XLSX.writeFile(workbook, `${safe_csv_filename(form_table.form?.name)}.xlsx`);
}
